import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import { Badge, Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import {
  GridColumnVisibilityModel,
  GridDensity,
  gridFilteredSortedRowIdsSelector,
  GridPreferencePanelsValue,
  Toolbar,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import { useActiveUser } from 'api/slack/useActiveUser'
import ChangeBoardView from 'components/DataGrid/ChangeBoardView'
import CustomDensityMenu from 'components/DataGrid/components/CustomDensityModal'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import { ncoStatusFilters } from 'components/DataGrid/filterArrays'
import { saveModel } from 'components/DataGrid/operators/loadModels'
import QuickFilterComponent from 'components/DataGrid/QuickFilterComponent'
import PivotPanelComponent from 'components/MTB/grid/PivotPanelTrigger'
import SelectMonthRangeFilter from 'components/MTB/grid/SelectMonthRangeFilter'
import SelectChangeTypeFilter, { type ChangeTypeValue } from 'components/ProductRows/SelectChangeTypeFilter'
import ViewFilterButtons from 'components/MTB/ViewFilterButtons'
import { filter_model_nco } from 'components/NCO/defaultModels'
import NcoCustomViews from 'components/NCO/NcoCustomViews'
import NcoFilters from 'components/NCO/NcoFilters'
import { filter_model_projects } from 'components/Projects/defaultConfigs'
import ProductDateRangeFilter, {
  DATE_RANGE_PREFIX,
  type DateRangeValue,
} from 'components/ProductRows/ProductDateRangeFilter'
import { filter_model_team_tasks } from 'components/TeamTasks/grid/defaultModels'
import TeamTasksCustomFilter from 'components/TeamTasks/grid/TeamTasksCustomFilters'
import ChangeViewSelect from 'components/ui/change-view-select'
import ExportCsvButton from 'components/ui/export-csv-button'
import FilterMyDataButton from 'components/ui/filter-my-data-button'
import OpenColumnsMenu from 'components/ui/open-columns-menu'
import OpenFiltersMenu from 'components/ui/open-filters-menu'
import ResetColumnWidths from 'components/ui/reset-column-widths-button'
import ResetFiltersButton from 'components/ui/reset-filters-button'
import ResetGridColumns from 'components/ui/reset-grid-columns'
import ResetSortingButton from 'components/ui/reset-sorting-button'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import ResetColumnOrder from 'theme/components/ResetColumnOrder'
import { GlobalGridToolbarProps } from 'types'
import { useChangeView } from 'utils/useChangeView'
import DealsFilter from '../../components/Deals/grid/DealsFilters'
import OrganizationsFilters from '../../components/Organizations/grid/OrganizationsFilters'
import ProductsFilters from '../../components/ProductRows/ProductsFilters'
import DensityMenu from './DensityMenu'
import { exportXlsx } from './operators/exportOperators'

const isSortModelEqual = (sortModel1, sortModel2) => {
  if (sortModel1?.length !== sortModel2?.length) return false
  return sortModel1?.every((sortItem, index) => {
    const initialSortItem = sortModel2[index]
    return sortItem.field === initialSortItem.field && sortItem.sort === initialSortItem.sort
  })
}

const GlobalGridToolbar: React.FC<GlobalGridToolbarProps> = ({
  filterModel,
  initialVisibilityState,
  initialSortModel,
  sortModel,
  columnWidths,
  setFilterModel,
  setColumnVisibilityModel,
  onClearSorting,
  onClearVisibility,
  onChangeVisibility,
  onClearWidths,
  onClearColumnOrder,
  columnOrder,
  defaultColumnOrder,
  onChangeDensity,
  onChangeView,
  selectedView,
  setSelectedView,
  customView,
  setCustomView,
  context,
  boardTheme,
  onChangeMonth,
  monthFilter,
  onChangeChangeType,
  changeType,
  syncStatus,
  totalRowCount,
}) => {
  const theme = useTheme()
  const handleChangeView = useChangeView()
  const [expanded, setExpanded] = React.useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [dateAnchorEl, setDateAnchorEl] = useState(null)
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<string | null>(null)
  const [invoicedActive, setInvoicedActive] = useState(false)
  const [notInvoicedActive, setNotInvoicedActive] = useState(false)
  const [densityAnchorEl, setDensityAnchorEl] = useState(null)
  const [viewsAnchorEl, setViewsAnchorEl] = useState(null)
  const [isCustomFiltersOpen, setCustomFiltersOpen] = useState(false)
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false)
  const [isCustomViewsOpen, setCustomViewsOpen] = useState(false)
  const [syncHovered, setSyncHovered] = useState(false)
  const { activeUser, isActiveUserLoading, isActiveUserError: _isActiveUserError } = useActiveUser()
  const { profile } = activeUser?.user ?? {}
  const activeUserName =
    activeUser && activeUser.user && activeUser.user.profile ? activeUser.user.profile.real_name : null
  const isSortModelChanged = useMemo(() => {
    return !isSortModelEqual(sortModel, initialSortModel)
  }, [sortModel, initialSortModel])
  const apiRef = useGridApiContext() // Get DataGrid API

  // 🔹 Toggle Columns Visibility Menu
  const handleOpenColumnsMenu = () => {
    apiRef.current.showPreferences('columns' as GridPreferencePanelsValue)
  }

  // 🔹 Toggle Filter Panel
  const handleOpenFilterPanel = () => {
    apiRef.current.showFilterPanel()
  }

  // 🔹 Export Data
  const handleExport = () => {
    // apiRef.current.exportDataAsCsv()
    const _rowIds = gridFilteredSortedRowIdsSelector(apiRef)
    if (context === 'organizations') {
      const NUMERIC_FIELDS = ['price', 'mediaSpend', 'grossProfit']
      // 🔹 parent totals blank
      // exportXlsx(context, apiRef, ['price','mediaSpend','grossProfit'], 'name', undefined, 'blank', 'visible');

      // 🔹 parent totals as static values
      exportXlsx(context, apiRef, NUMERIC_FIELDS, 'name', undefined, 'value', 'visible')

      // 🔹 parent totals as live formulas
      // exportXlsx(context, apiRef, ['price','mediaSpend','grossProfit'], 'name', undefined, 'formula', 'visible');

      return
    }

    return apiRef.current.exportDataAsCsv({
      getRowsToExport: params => {
        const allRowIds = params.apiRef.current.getAllRowIds()

        const orgOnlyRowIds = allRowIds.filter(id => {
          // Only include IDs without '::MONTHLY_TASK_' = parent/org rows
          return typeof id === 'string' && !id.includes('::MONTHLY_TASK_')
        })

        return orgOnlyRowIds
      },
    })
  }
  // 🔹🔹🔹 Filter model persistance 🔹🔹🔹 //
  const setAndPersistFilterModel = (updater: ((prev: any) => any) | any) => {
    setFilterModel(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      // ✅ Persist to local storage just like quickFilterValues
      saveModel(context, 'FilterModel', next)
      return next
    })
  }

  ///////////////////////////////////////////////////////////////////////////\
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isTriggerCombo = (isMac ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === 'f'

      if (isTriggerCombo) {
        event.preventDefault()
        setExpanded(prev => !prev) // 🔁 toggles open/close
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
  const handleFilterMyData = () => {
    // Check if "My Items" filter is already active (check quickFilterValues for the user's name)
    const isActive = filterModel?.quickFilterValues?.length === 1 && filterModel.quickFilterValues[0] === activeUserName

    if (isActive) {
      // Toggle off - clear the quick filter
      const newModel = {
        ...filterModel,
        quickFilterValues: [],
      }
      setFilterModel(newModel)
      saveModel(context, 'FilterModel', newModel)
    } else {
      // Toggle on - use quick filter with user's name
      // The quick filter searches across owner, team roles, and other relevant fields
      const newModel = {
        ...filterModel,
        quickFilterValues: [activeUserName],
      }
      setFilterModel(newModel)
      saveModel(context, 'FilterModel', newModel)
    }
  }

  const handleChangeColumnView = (newVisibilityState: GridColumnVisibilityModel, newVisibilityTitle?: string) => {
    onChangeVisibility(newVisibilityState)
  }
  const handleOpenCustomFilters = event => {
    setAnchorEl(event.currentTarget) // Attach the popover to the button
    setCustomFiltersOpen(true)
  }
  const handleOpenDateFilter = event => {
    setDateAnchorEl(event.currentTarget) // Attach the popover to the button
    setIsDateFilterOpen(true)
  }
  const handleOpenCustomViews = event => {
    setViewsAnchorEl(event.currentTarget) // Attach the popover to the button
    setCustomViewsOpen(true)
  }
  const handleCloseCustomFilters = () => {
    setAnchorEl(null) // Clear the anchor when closing
    setCustomFiltersOpen(false)
  }
  const handleCloseDateFilter = () => {
    setDateAnchorEl(null) // Clear the anchor when closing
    setIsDateFilterOpen(false)
  }
  const handleCloseCustomViews = () => {
    setViewsAnchorEl(null) // Clear the anchor when closing
    setCustomViewsOpen(false)
  }
  const openDensityMenu = event => {
    setDensityAnchorEl(event.currentTarget)
  }
  const closeDensityMenu = () => {
    setDensityAnchorEl(null)
  }
  const changeDensity = (newDensity: GridDensity) => {
    apiRef.current.setDensity(newDensity)
    onChangeDensity(newDensity)
  }
  const clearFilters = () => {
    setFilterModel(
      context === 'projects'
        ? filter_model_projects
        : context === 'teamtasks'
        ? filter_model_team_tasks
        : context === 'nco'
        ? filter_model_nco
        : { items: [], quickFilterValues: [] }
    )
    // Pass '' (not null) so persistence writes a valid sentinel instead of
    // the literal string "null", which would land outside the Select's
    // available options and trigger the MUI "out-of-range value" warning.
    if (onChangeMonth) onChangeMonth('')
    if (onChangeChangeType) onChangeChangeType('')
    saveModel(
      context,
      'FilterModel',
      context === 'projects'
        ? filter_model_projects
        : context === 'nco'
        ? filter_model_nco
        : { items: [], quickFilterValues: [] }
    )
  }
  const handleSetFilter = (fieldName: string, selectedValues: string | string[]) => {
    // Normalize value to array for `isAnyOf`
    const valuesArray = Array.isArray(selectedValues) ? selectedValues : selectedValues ? [selectedValues] : []

    setAndPersistFilterModel(prev => {
      // Remove any existing item for this field
      const items = (prev?.items ?? []).filter(item => item.field !== fieldName)

      // If we have selections, add a fresh item; otherwise we’re just removing it
      if (valuesArray.length > 0) {
        items.push({
          id: `${fieldName}-${Date.now()}`,
          field: fieldName,
          operator: 'isAnyOf',
          value: valuesArray,
        })
      }

      return {
        ...prev,
        items,
      }
    })
  }

  const handleSetCustomFilters = (fieldName, operator, newValue) => {
    setAndPersistFilterModel(prev => {
      const items = (prev?.items ?? []).filter(item => item.field !== fieldName)
      items.push({
        id: `${fieldName}-${Date.now()}`,
        field: fieldName,
        operator,
        value: newValue,
      })
      return { ...prev, items }
    })
  }

  // Convert the simplified {start, end} payload from ProductDateRangeFilter
  // into the two filter items the backend already knows how to apply (the
  // overlap pair). Items are tagged with DATE_RANGE_PREFIX ids so we can
  // replace them cleanly without disturbing the month-range items, which use
  // the same field+operator combos but with different ids.
  const handleSetProductDateRange = ({ start, end }: DateRangeValue) => {
    setAndPersistFilterModel(prev => {
      const items = (prev?.items ?? []).filter(
        (i: { id?: string | number }) => !i.id?.toString().startsWith(DATE_RANGE_PREFIX)
      )
      if (end) {
        items.push({
          id: `${DATE_RANGE_PREFIX}active-to`,
          field: 'startDate',
          operator: 'onOrBefore',
          value: end,
        })
      }
      if (start) {
        items.push({
          id: `${DATE_RANGE_PREFIX}active-from`,
          field: 'endDate',
          operator: 'onOrAfter',
          value: start,
        })
      }
      return { ...prev, items }
    })
  }

  const handleFilterInvoiced = () => {
    setInvoicedActive(true)
    setNotInvoicedActive(false)
    handleSetCustomFilters('invoiceStatus', 'equals', 'Invoiced')
  }
  const handleFilterNotInvoiced = () => {
    setNotInvoicedActive(true)
    handleSetCustomFilters('invoiceStatus', 'equals', 'Not Invoiced')
    setInvoicedActive(false)
  }
  const handleChangeVisibility = (newVisibilityState: GridColumnVisibilityModel) => {
    onChangeVisibility(newVisibilityState)
  }
  const handleClearVisibility = () => {
    onClearVisibility()
    setCurrentView(null)
    setCustomView && setCustomView?.('default')
  }

  return (
    <Toolbar>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignContent: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            ml: 2,
          }}
        >
          {/* Server-side sync status indicator */}
          {syncStatus && (
            <Box
              onMouseEnter={() => setSyncHovered(true)}
              onMouseLeave={() => setSyncHovered(false)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'default',
                transition: 'all 0.2s ease-in-out',
                overflow: 'hidden',
                minWidth: syncHovered ? 'auto' : 12,
                maxWidth: syncHovered ? 300 : 12,
                bgcolor: syncHovered ? (syncStatus.isInitialized ? 'success.main' : 'warning.main') : 'transparent',
                color: syncHovered
                  ? syncStatus.isInitialized
                    ? 'success.contrastText'
                    : 'warning.contrastText'
                  : 'inherit',
                borderRadius: 1,
                px: syncHovered ? 1 : 0,
                py: syncHovered ? 0.5 : 0,
              }}
              title={
                syncStatus.lastSyncTime
                  ? `Last sync: ${new Date(syncStatus.lastSyncTime).toLocaleString()}`
                  : 'Not synced yet'
              }
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: syncStatus.isInitialized ? '#4caf50' : '#ff9800',
                  flexShrink: 0,
                  animation: !syncStatus.isInitialized ? 'pulse 1s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  },
                }}
              />
              {syncHovered && (
                <>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {syncStatus.isInitialized ? 'SYNCED' : 'SYNCING...'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      ml: 0.5,
                    }}
                  >
                    {(totalRowCount ?? 0).toLocaleString()} of {(syncStatus.documentCount ?? 0).toLocaleString()}{' '}
                    indexed
                  </Typography>
                </>
              )}
            </Box>
          )}
          {context === 'deals' && (
            <Tooltip title="View KanBan">
              <IconButton onClick={() => navigate('/deals/pipeline')}>
                <ViewKanbanIcon sx={{ height: 30, width: 30, color: 'rgb(251, 193, 30)' }} />
              </IconButton>
            </Tooltip>
          )}
          {onChangeView && (
            <ChangeViewSelect
              onChangeView={(newView: string) => onChangeView(newView, setFilterModel, setColumnVisibilityModel)}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
            />
          )}
          {['nco', 'mtb', 'projects', 'teamtasks']?.includes(context) && (
            <ChangeBoardView onChangeView={newView => handleChangeView({ newView })} />
          )}
          <Tooltip title={<Typography sx={{ fontSize: 14 }}>Density</Typography>}>
            <IconButton onClick={openDensityMenu}>
              <FormatAlignJustifyIcon />
            </IconButton>
          </Tooltip>
          <CustomDensityMenu anchorEl={densityAnchorEl} onClose={closeDensityMenu}>
            <DensityMenu changeDensity={changeDensity} />
          </CustomDensityMenu>
          <OpenColumnsMenu onOpenColumns={handleOpenColumnsMenu} />
          <OpenFiltersMenu onOpenFilters={handleOpenFilterPanel} filterModel={filterModel} badgeColor={boardTheme} />
          <ExportCsvButton onExport={handleExport} />
          <PivotPanelComponent />
        </Box>
        <Box
          className="custom-filters-container"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            overflow: 'visible',
            // Give floating InputLabels (Month, Change Type) room to translate
            // above their fields without being clipped by the Toolbar's top edge.
            pt: 1,
          }}
        >
          {onChangeMonth && <SelectMonthRangeFilter value={monthFilter ?? ''} onChange={onChangeMonth} />}
          {onChangeChangeType && (
            <SelectChangeTypeFilter
              value={(changeType ?? '') as ChangeTypeValue}
              onChange={onChangeChangeType}
            />
          )}
          {customView && (
            <TeamTasksCustomFilter
              isCustomViewsOpen={isCustomViewsOpen}
              customView={customView}
              setCustomView={setCustomView}
              viewsAnchorEl={viewsAnchorEl}
              onOpenCustomViews={handleOpenCustomViews}
              onClose={handleCloseCustomViews}
              onChangeVisibility={onChangeVisibility}
              filterModel={filterModel}
              setFilterModel={setFilterModel}
            />
          )}
          {context === 'nco' && (
            <>
              <NcoCustomViews
                currentView={currentView}
                setCurrentView={setCurrentView}
                onChangeVisibility={handleChangeVisibility}
                isOpen={isCustomViewsOpen}
                anchorEl={viewsAnchorEl}
                onOpenViews={handleOpenCustomViews}
                onClose={handleCloseCustomViews}
              />
              <NcoFilters
                filterModel={filterModel}
                options={ncoStatusFilters}
                value={statusFilter}
                onChangeFilter={handleSetFilter}
                setterFn={setStatusFilter}
                isOpen={isCustomFiltersOpen}
                anchorEl={anchorEl}
                onOpenFilters={handleOpenCustomFilters}
                onClose={handleCloseCustomFilters}
                context={'onboardingStatus'}
              />
            </>
          )}
          {context === 'mtb' && (
            <>
              <NcoCustomViews
                currentView={currentView}
                setCurrentView={setCurrentView}
                onChangeVisibility={handleChangeVisibility}
                isOpen={isCustomViewsOpen}
                anchorEl={viewsAnchorEl}
                onOpenViews={handleOpenCustomViews}
                onClose={handleCloseCustomViews}
              />
              <CustomFiltersModal
                isOpen={isCustomViewsOpen}
                anchorEl={viewsAnchorEl}
                handleCloseCustomFilters={handleCloseCustomViews}
              >
                <ViewFilterButtons onChangeColumnView={handleChangeColumnView} />
              </CustomFiltersModal>
            </>
          )}
          {context === 'deals' && (
            <DealsFilter
              filterModel={filterModel}
              onSetFilter={handleSetFilter}
              isOpen={isCustomFiltersOpen}
              anchorEl={anchorEl}
              onOpenFilters={handleOpenCustomFilters}
              onClose={handleCloseCustomFilters}
            />
          )}
          {context === 'organizations' && (
            <OrganizationsFilters
              filterModel={filterModel}
              onSetFilter={handleSetFilter}
              isOpen={isCustomFiltersOpen}
              anchorEl={anchorEl}
              onOpenFilters={handleOpenCustomFilters}
              onClose={handleCloseCustomFilters}
            />
          )}
          {context === 'products' && (
            <>
              <ProductsFilters
                filterModel={filterModel}
                onSetFilter={handleSetFilter}
                isOpen={isCustomFiltersOpen}
                anchorEl={anchorEl}
                onOpenFilters={handleOpenCustomFilters}
                onClose={handleCloseCustomFilters}
              />
              <ProductDateRangeFilter
                filterModel={filterModel}
                onChange={handleSetProductDateRange}
                isOpen={isDateFilterOpen}
                anchorEl={dateAnchorEl}
                onOpenFilters={handleOpenDateFilter}
                onClose={handleCloseDateFilter}
              />
            </>
          )}
          {context === 'accounting' && (
            <>
              <Button
                sx={{
                  height: 40,
                  color: theme.palette.text.success,
                }}
                onClick={handleFilterInvoiced}
              >
                <Typography
                  sx={{ fontWeight: invoicedActive ? 'bold' : '', fontStyle: invoicedActive ? 'oblique' : '' }}
                >
                  Invoiced
                </Typography>
              </Button>
              <Button
                sx={{
                  height: 40,
                  color: theme.palette.text.warning,
                }}
                onClick={handleFilterNotInvoiced}
              >
                <Typography
                  sx={{ fontWeight: notInvoicedActive ? 'bold' : '', fontStyle: notInvoicedActive ? 'oblique' : '' }}
                >
                  Not Invoiced
                </Typography>
              </Button>
            </>
          )}
          <FilterMyDataButton
            profile={profile}
            icon={
              <EntityAvatar
                name={profile?.name || ''}
                photoUrl={profile?.image_512}
                size="md"
                showName={false}
                isLoading={isActiveUserLoading}
              />
            }
            filterModel={filterModel}
            onClick={handleFilterMyData}
            label={'My Items'}
          />
          {onClearColumnOrder && columnOrder && defaultColumnOrder && (
            <ResetColumnOrder
              columnOrder={columnOrder}
              onClearColumnOrder={onClearColumnOrder}
              defaultColumnOrder={defaultColumnOrder}
            />
          )}
          {onClearWidths && columnWidths && (
            <ResetColumnWidths columnWidths={columnWidths} onResetWidths={onClearWidths} />
          )}
          <ResetGridColumns
            apiRef={apiRef}
            initialVisibilityState={initialVisibilityState}
            onClearVisibility={handleClearVisibility}
          />
          <ResetSortingButton isSortModelChanged={isSortModelChanged} onClearSorting={onClearSorting} />
          <ResetFiltersButton filterModel={filterModel} handleClearFilters={clearFilters} />
          <Badge
            badgeContent={filterModel?.quickFilterValues?.length}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              overflow: 'visible',
              '& .MuiBadge-badge': {
                display: filterModel?.quickFilterValues?.length ? 'flex' : 'none',
                bgcolor: boardTheme,
                color: 'rgba(0,0,0,1)',
                fontWeight: 'bold',
                fontSize: 12,
                minWidth: '20px',
                height: '20px',
                transform: 'translate(-6px, -6px)',
              },
            }}
          >
            <QuickFilterComponent expanded={expanded} setExpanded={setExpanded} />
          </Badge>
        </Box>
      </Box>
    </Toolbar>
  )
}

export default GlobalGridToolbar
